import { MediaObjectUrl, MediaVariantEnum } from '@kudos/http-client'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import { SxProps } from '@mui/material/styles'
import { fullNameFromParts, initialsForName } from '@shared/utils/nameFormatting'
import { useT } from '@transifex/react'
import { ReactNode } from 'react'
import AvatarWrapper from './AvatarWrapper'
import useAvatarConfig from './useAvatarConfig'

export type AvatarSize = number

export type AvatarFontUnit = 'px' | 'vh'

type UserAvatarProps = {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  size?: AvatarSize
  media?: MediaObjectUrl | null
  /* optional override for images that do not have avatar variants */
  mediaVariant?: MediaVariantEnum
  src?: string
  fontUnit?: AvatarFontUnit
  hideDropShadow?: boolean
  defaultIcon?: ReactNode
  avatarBoxStyle?: SxProps
  isActive?: boolean
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  firstName,
  lastName,
  fullName,
  size = 40,
  media,
  mediaVariant,
  src,
  fontUnit = 'px',
  hideDropShadow = false,
  defaultIcon,
  avatarBoxStyle = {},
  isActive = true,
}) => {
  const t = useT()
  const { avatarUrl } = useAvatarConfig(size, media || null, mediaVariant)
  const firstname = fullName?.split(' ')[0] ?? firstName
  const lastname = fullName?.split(' ')[1] ?? lastName

  const renderAvatarContent = () => {
    if (!!src || !!avatarUrl) {
      return null
    }
    return initialsForName(firstname, lastname) || defaultIcon || <PersonOutlineOutlinedIcon />
  }

  return (
    <AvatarWrapper
      size={size}
      media={media}
      src={src}
      alt={t('User profile avatar image')}
      fontUnit={fontUnit}
      hideDropShadow={hideDropShadow}
      avatarBoxStyle={{
        ...avatarBoxStyle,
        filter: isActive ? 'none' : 'grayscale(95%)',
        opacity: isActive ? 1 : 0.75,
      }}
      tooltipTitle={fullName ?? fullNameFromParts(firstName, lastName)}>
      {renderAvatarContent()}
    </AvatarWrapper>
  )
}

export default UserAvatar
